import type { Exercise } from "./schemas/exercise.js";
import type {
  AthleteTargets,
  Program,
  TargetAxis,
  TargetMetric,
} from "./schemas/program.js";
import { targetBand } from "./schemas/program.js";
import type { TimedSessionResult } from "./progressTypes.js";
import type { UnitsPreference } from "./schemas/profile.js";
import { formatVolume, type WeightUnitLabels } from "./units.js";
import {
  hardSetsByMuscle,
  uncataloguedExerciseSlugs,
  weeklyActualFor,
  bodyweightMissingSlugs,
} from "./weeklyTargetActual.js";
import { catalogBySlug, resolveExerciseMuscles } from "./volumeByMuscle.js";
import { weekProgress, weekWindowStart, type WeekProgress } from "./weekWindow.js";
import { pickAuthoredText } from "./authoredText.js";
import type { Locale } from "./locale.js";

// Format ONE weekly-target metric value for display — the single place that
// knows a tonnage value is a weight (kg/lb, compacted) and a hard-set/rep value
// is a bare count. Used by the meter card AND the editor's compact rows so the
// two never diverge on how a tonnage goal reads. Counts render at ≤1dp
// (locale-grouped): hard-set actuals are fractional since AP-6's synergist
// credit, so "8.4 / 12" — while whole values still read as "12", not "12.0".
export const formatTargetMetricValue = (
  value: number,
  metric: TargetMetric,
  unit: UnitsPreference,
  locale = "en-US",
  labels: WeightUnitLabels,
): string =>
  metric === "tonnage"
    ? // DS-88 — a weekly tonnage is a SUM, not a scale reading, so it takes
      // formatVolume's whole number and not formatWeight's 2dp-for-kg
      // default (for single plate readings). Antenore's smoke read
      // "avg 6,308.498 kg/wk". Same class as DS-3.
      formatVolume(value, unit, locale, labels)
    : new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(value);

// The formatted "actual / target" pair for one row — the single place both the
// expanded <TargetMeterRow> and the in-workout strip's collapsed glance format
// the value pair, so the two can't drift on how a tonnage vs hard-set value
// reads. `target` is null when the row carries no goal (degraded/neutral bar).
export const formatActualTarget = (
  row: Pick<WeeklyTargetRow, "actual" | "target" | "metric">,
  unit: UnitsPreference,
  locale = "en-US",
  labels: WeightUnitLabels,
): { actual: string; target: string | null } => ({
  actual: formatTargetMetricValue(row.actual, row.metric, unit, locale, labels),
  target:
    row.target != null
      ? formatTargetMetricValue(row.target, row.metric, unit, locale, labels)
      : null,
});

// AP-3 — the three-zone weekly meter scorer. Pure: scores ACTUAL training
// (hard sets per MUSCLE over the current calendar week) against the pinned
// program's weekly `Program.targets`. Net-new — no prior scorer exists.
//
// Axis reworked 2026-07-07 (use-driven): the meter scores `muscle` and
// `exercise`, not `pattern`. Pattern lumps very different exercises (hack vs
// back squat) and is meaningless for tonnage; muscle absorbs exercise rotation
// because a hard set of any squat variant counts toward quadriceps
// (hardSetsByMuscle attributes each exercise's hardSetCount to the muscles it
// substantially trains), exercise pins a fixed lift you don't rotate.
//
// Scored metrics (AP-1b-u, 2026-07-07): hard-sets AND tonnage, on either axis —
// each row carries its `metric` so the host formats the value in the right unit.
// The band/zone logic is unit-agnostic (actual vs target×band). Only `reps` and
// the legacy `pattern` axis don't score (the editor tags them "not in meter").

export type TargetZone = "under" | "in-band" | "over";

export type WeeklyTargetRow = {
  // The bucket key: a CatalogMuscleSlug (axis=muscle) or an exercise slug
  // (axis=exercise). The host resolves the label per axis (muscleKey for
  // muscle, an exercise-name resolver for exercise). Unknown key scores 0.
  key: string;
  // How the host labels + formats the row: `axis` picks the label resolver,
  // `metric` picks the unit (hard-sets = bare count, tonnage = kg/lb weight).
  axis: TargetAxis;
  metric: TargetMetric;
  // The actual trained this calendar week, in the metric's unit
  // (hard-set count, or tonnage in canonical kg).
  actual: number;
  // Weekly hard-set target, or null when this muscle carries no target
  // (degraded mode, or a trained-but-untargeted muscle in scored mode).
  target: number | null;
  // In-band edges = target × band multipliers. Null when target is null.
  bandLow: number | null;
  bandHigh: number | null;
  // Which zone `actual` falls in. Null when there is no target to score.
  zone: TargetZone | null;
  // AP-9 — the pinned program prescribes ~0 hard sets for THIS targeted muscle:
  // you set a goal the program never trains. A pure DATA fact (from the passed
  // prescription), NOT a visual decision — the UI turns it into the distinct
  // "no coverage" label + inert bar, apart from a "building 0/N" (prescribed,
  // not trained yet). Always false for untargeted/degraded rows and when the
  // host didn't wire the prescription.
  noCoverage: boolean;
  // ME-6 — the pinned program's weekly prescription for THIS row's muscle: the
  // 2nd reference the meter draws as a distinct dashed mark ("the plan
  // prescribes X this week"). Three references on one row, all different marks:
  // `actual` (trained, solid fill) · `prescribed` (the plan, dashed) · `target`
  // (the athlete's stable goal, dark caret). Null for exercise-axis rows,
  // untargeted rows, or when no program is pinned (no prescription passed) —
  // then only the target shows, and "under during a deload" can't be an alarm
  // because the athlete's target hasn't moved.
  prescribed: number | null;
  // Absent reads false. True = the real number may be higher: a muscle
  // hard-set row whose window holds sets of an exercise the catalog lacks, or
  // a row a session computed with no bodyweight undercounts (#116).
  incomplete?: boolean;
  incompleteReasons?: IncompleteReason[];
};

// Why a row is incomplete. Absent `incompleteReasons` on an incomplete row
// reads ["uncatalogued"], the only reason before #116 case 2; the list is
// written whenever "no-bodyweight" is among the reasons.
export type IncompleteReason = "uncatalogued" | "no-bodyweight";

// What makes a row incomplete in one window, worked out once (#116). Both
// metrics read the bodyweight (a bodyweight set counted no hard set and no
// tonnage), so the weight reason follows the exercises the sessions listed
// and the muscles those exercises train.
export type IncompleteSource = {
  uncatalogued: boolean;
  bodyweightExercises: string[];
  bodyweightMuscles: string[];
};

const incompleteFields = (reasons: IncompleteReason[]) =>
  reasons.length === 0
    ? {}
    : reasons.includes("no-bodyweight")
      ? { incomplete: true, incompleteReasons: reasons }
      : { incomplete: true };

// The incomplete fields of one row: the rows built here and the synthetic
// zero rows of the coverage census read the same rule.
export const incompleteFieldsFor = (
  src: IncompleteSource | undefined,
  item: { axis: TargetAxis; key: string; metric: TargetMetric },
) => {
  if (!src) return {};
  const r: IncompleteReason[] = [];
  if (src.uncatalogued && item.axis === "muscle" && item.metric === "hard-sets") r.push("uncatalogued");
  const affected =
    item.axis === "muscle" ? src.bodyweightMuscles : item.axis === "exercise" ? src.bodyweightExercises : [];
  if (affected.includes(item.key) && (item.metric === "hard-sets" || item.metric === "tonnage"))
    r.push("no-bodyweight");
  return incompleteFields(r);
};

export type WeeklyTargetsData = {
  // `scored` = the pinned program has ≥1 muscle/hard-set target and the bars
  // show zones + the target marker. `degraded` = no such target; the card
  // still shows the top trained muscles as neutral bars (value from day one).
  mode: "scored" | "degraded";
  // The pinned program's name (the "active block"). Null when nothing pinned.
  blockName: string | null;
  rows: WeeklyTargetRow[];
  // Summary counters (scored mode): how many targeted muscles are in-band.
  onTargetCount: number;
  targetCount: number;
  // AP-9 — this week's hard sets per muscle, the SAME map the rows are scored
  // from. Surfaced because the external MEV/MAV/MRV band (volumeLandmarks.ts)
  // reads exactly this shape on exactly this window, and re-deriving it at the
  // callsite would mean a second `hardSetsByMuscle` pass that could silently
  // drift from the one behind the bars. ⛔ Not a new computation: the same
  // `byMuscle` that already scores every row, given a way out.
  hardSetsByMuscle: Record<string, number>;
  // §8bis.1: how far into this week the window is: every surface that shows
  // these numbers says the week is partial (Tuesday · 5 days to go), because
  // the window above is SHORT early in the week. On the SAME `weekStartDay`.
  week: WeekProgress;
  // #116: the window holds sets of an exercise the catalog lacks. True even
  // when no muscle row exists to carry the flag (empty degraded result), so the
  // host says "incomplete", not "no data", and can flag synthetic rows. Also
  // true when a session of the window was computed with no bodyweight.
  incomplete?: boolean;
  incompleteReasons?: IncompleteReason[];
  // Present when a session of the window was computed with no bodyweight:
  // the per-row rule's input, for rows built outside (the census zero rows).
  incompleteSource?: IncompleteSource;
};

// Cap the row count so the card stays glanceable on a phone. Scored mode
// keeps every targeted muscle (they're deliberate) and tops up with a few
// trained-but-untargeted muscles; degraded mode shows the busiest muscles.
const MAX_ROWS = 8;
const MAX_DEGRADED_ROWS = 5;

// Exported so the program-target projection (AP-10) tags each projected weekly
// total with the SAME three-zone verdict the meter shows, from one definition.
export const zoneFor = (actual: number, lo: number, hi: number): TargetZone =>
  actual < lo ? "under" : actual > hi ? "over" : "in-band";

// CO-26 — "hit the target" means in-band OR over: sforare non è disobbedire,
// hanno fatto almeno quello che era prescritto. This is the ONE place that
// answers "is this zone on target" — the athlete's own onTargetCount below AND
// the coach dashboard's hitCount (coachDashboard.ts) both read it, so the two
// counters can no longer diverge by having the same condition typed twice.
export const isOnTarget = (zone: TargetZone | null): boolean =>
  zone === "in-band" || zone === "over";

/**
 * Score this week's hard-set training against a pinned program's targets.
 * Returns null when there is genuinely nothing to show (no targets AND no
 * trained muscles) so the host can hide the card, exactly like `weekly`.
 */
export const computeWeeklyTargets = (
  sessions: TimedSessionResult[],
  catalog: ReadonlyArray<Exercise>,
  program: Program | null,
  nowMs: number,
  // AP-9 — the program's per-muscle weekly hard-set prescription
  // (programWeeklyPrescription). The caller memoizes it on [program, catalog]
  // and passes it so a targeted muscle the program never trains reads "no
  // coverage", not "building 0/N". Absent (null) → noCoverage stays false
  // everywhere (backward compatible). Muscle axis only: the map is per-muscle,
  // so an exercise-axis key would never match — its noCoverage stays false.
  prescription: Record<string, number> | null = null,
  // Row-count cap + the athlete's week-start day. `maxRows` omitted → the
  // compact defaults (8 scored / 5 degraded) that keep the Home glance card +
  // in-workout strip phone-glanceable; PG-1 Progress passes a high value for the
  // FULL per-muscle census (it re-sorts the rows attention-first itself).
  // `weekStartDay` (PG-2) sets which weekday the weekly window resets on;
  // omitted → Monday. It moves the window boundary and the cap; the scoring
  // rule itself does not depend on it.
  // ⭐ Every measurement that says "week" must ask this field WHICH week it
  // means — a window that assumes Monday is already wrong for an athlete who
  // resets on Thursday (Antenore, 2026-09-15).
  opts: { maxRows?: number; weekStartDay?: number } = {},
  // ME-6/ME-3 — the ATHLETE's weekly targets (the AthleteTargets doc). ITS items
  // are the sole target source: the target belongs to the athlete now, and the
  // pinned `program` stays only for the block name + the prescription 2nd
  // reference. null/undefined (loading, or a surface with no target source) →
  // no targets (degraded), exactly like an empty items list. Every callsite is
  // repointed onto this — Program.targets is no longer read here.
  athleteTargets: AthleteTargets | null = null,
  // Reader's language (PE-21): the program's authored text is resolved
  // reader → author → any available. Omitted → the chain starts at the
  // author, never at English.
  locale?: Locale,
): WeeklyTargetsData | null => {
  const scoredCap = opts.maxRows ?? MAX_ROWS;
  const degradedCap = opts.maxRows ?? MAX_DEGRADED_ROWS;
  // The window of the GOAL is the CURRENT calendar week [weekStart, now]
  // (PG-2): a weekly target resets on a fixed weekday, so the actual can't
  // silently drop as sessions age out mid-week.
  // ⛔ It is the target's window, not the athlete's: the per-muscle
  // acute/chronic state is measured elsewhere and never resets (2026-09-15).
  // ⚠️ Early in the week this window is SHORT — two days on a Tuesday — so a
  // caller that renders "below MEV" off it without saying the week is partial
  // is reporting the weekday, not the athlete. 📖 measurement-contract §8bis.
  const fromMs = weekWindowStart(nowMs, opts.weekStartDay);
  const week = weekProgress(nowMs, opts.weekStartDay);
  // PG-16 — built ONCE here and threaded down. `weeklyActualFor` below runs
  // per scoreable row, and used to rebuild this same Map from this same
  // unchanged catalog on every one of them.
  const index = catalogBySlug(catalog);
  const byMuscle = hardSetsByMuscle(sessions, catalog, fromMs, nowMs, index);
  const hardOf = (m: string): number => byMuscle[m] ?? 0;
  // Unknown exercises credit no muscle; which muscle they train is unknowable,
  // so every muscle hard-set row is flagged incomplete (counts stay as is).
  // #116 case 2: a session computed with no bodyweight undercounts the
  // exercises it lists (both metrics, hard sets and tonnage, read the weight),
  // and through them the muscles they train. An exercise the catalog lacks
  // has no known muscle: it stays the catalog reason, never the weight one.
  const missing = bodyweightMissingSlugs(sessions, fromMs, nowMs);
  const hasUncatalogued =
    uncataloguedExerciseSlugs(sessions, index, fromMs, nowMs).length > 0 ||
    missing.some((slug) => !index.has(slug));
  const zeroBw = new Set(missing.filter((slug) => index.has(slug)));
  const source: IncompleteSource = {
    uncatalogued: hasUncatalogued,
    bodyweightExercises: [...zeroBw],
    bodyweightMuscles: [
      ...new Set([...zeroBw].flatMap((slug) => resolveExerciseMuscles(index.get(slug) as Exercise))),
    ],
  };
  const incompleteFor = (item: { axis: TargetAxis; key: string; metric: TargetMetric }) =>
    incompleteFieldsFor(source, item);
  const muscleHardSetFlag = (key: string) => incompleteFor({ axis: "muscle", key, metric: "hard-sets" });
  // The data-level flag: any reason in the window, even with no row to carry it.
  const anyIncomplete = hasUncatalogued || zeroBw.size > 0;
  const incompleteFlag = anyIncomplete
    ? {
        ...incompleteFields([
          ...(hasUncatalogued ? (["uncatalogued"] as const) : []),
          ...(zeroBw.size > 0 ? (["no-bodyweight"] as const) : []),
        ]),
        // Only when the weight is involved: the catalog reason alone needs no
        // per-row source (every muscle hard-set row takes it).
        ...(zeroBw.size > 0 ? { incompleteSource: source } : {}),
      }
    : {};

  // A muscle-axis target the program prescribes ~0 hard sets for. The map only
  // holds muscles the program trains (positive credit), so "absent or ~0" = no
  // coverage. A tiny synergist share still counts as covered (the honest read
  // there is "building", severely under) — hence a near-zero epsilon, not a
  // threshold.
  const isNoCoverage = (item: { axis: TargetAxis; key: string }): boolean =>
    item.axis === "muscle" &&
    prescription != null &&
    (prescription[item.key] ?? 0) < 1e-6;

  // The target items to score come solely from the athlete's own doc (ME-3).
  // null/empty → no scoreable targets (degraded), never the program's embedded
  // targets — those are retired.
  const targetItems = athleteTargets?.items ?? [];

  // Every target the meter can score: muscle/exercise × hard-sets/tonnage. An
  // unknown `key` simply scores 0 (weeklyActualFor never throws). `reps` and the
  // legacy `pattern` axis fall through to degraded, matching the editor's tag.
  const scoreable = targetItems.filter(
    (i) =>
      (i.axis === "muscle" || i.axis === "exercise") &&
      (i.metric === "hard-sets" || i.metric === "tonnage"),
  );

  if (scoreable.length > 0) {
    // Authored order — mixing hard-set counts (~12) and tonnage (~5000) under
    // one numeric sort is meaningless, so keep the editor's deliberate order.
    const targeted: WeeklyTargetRow[] = scoreable.map((item) => {
      const actual = weeklyActualFor(
        sessions,
        catalog,
        item,
        fromMs,
        nowMs,
        index,
      );
      const [loMult, hiMult] = targetBand(item);
      const bandLow = item.value * loMult;
      const bandHigh = item.value * hiMult;
      return {
        key: item.key,
        axis: item.axis,
        metric: item.metric,
        actual,
        target: item.value,
        bandLow,
        bandHigh,
        zone: zoneFor(actual, bandLow, bandHigh),
        noCoverage: isNoCoverage(item),
        ...incompleteFor(item),
        // The plan's prescription for this muscle = the 2nd reference. Muscle
        // axis + a passed prescription only; else null (no dashed mark). A
        // muscle the plan doesn't train reads 0 here (and noCoverage above).
        prescribed:
          item.axis === "muscle" && prescription != null
            ? (prescription[item.key] ?? 0)
            : null,
      };
    });

    // Top up with trained muscles carrying NO muscle-axis target, so the card
    // still shows the rest of the week's hard-set work as neutral bars. Exclude
    // any muscle already targeted (on either metric) so it never doubles up.
    const targetedMuscles = new Set(
      scoreable.filter((i) => i.axis === "muscle").map((i) => i.key),
    );
    const untargeted: WeeklyTargetRow[] = Object.keys(byMuscle)
      .filter((m) => !targetedMuscles.has(m) && hardOf(m) > 0)
      .sort((a, b) => hardOf(b) - hardOf(a))
      .map((m) => ({
        key: m,
        axis: "muscle" as const,
        metric: "hard-sets" as const,
        actual: hardOf(m),
        target: null,
        bandLow: null,
        bandHigh: null,
        zone: null,
        noCoverage: false,
        prescribed: null,
        ...muscleHardSetFlag(m),
      }));

    const rows = [...targeted, ...untargeted].slice(0, scoredCap);
    return {
      mode: "scored",
      blockName: pickAuthoredText(program?.name, locale, program?.authorLocale),
      rows,
      onTargetCount: targeted.filter((r) => isOnTarget(r.zone)).length,
      targetCount: targeted.length,
      hardSetsByMuscle: byMuscle,
      week,
      ...incompleteFlag,
    };
  }

  // Degraded: no scoreable target. Show the busiest trained muscles as
  // neutral bars — the meter earns its place before any target exists.
  const rows: WeeklyTargetRow[] = Object.keys(byMuscle)
    .filter((m) => hardOf(m) > 0)
    .sort((a, b) => hardOf(b) - hardOf(a))
    .slice(0, degradedCap)
    .map((m) => ({
      key: m,
      axis: "muscle" as const,
      metric: "hard-sets" as const,
      actual: hardOf(m),
      target: null,
      bandLow: null,
      bandHigh: null,
      zone: null,
      noCoverage: false,
      prescribed: null,
      ...muscleHardSetFlag(m),
    }));

  if (rows.length === 0 && !hasUncatalogued && zeroBw.size === 0) return null;
  return {
    mode: "degraded",
    blockName: pickAuthoredText(program?.name, locale, program?.authorLocale),
    rows,
    onTargetCount: 0,
    targetCount: 0,
    hardSetsByMuscle: byMuscle,
    week,
    ...incompleteFlag,
  };
};

// The two presentational transforms every WeeklyTargetsData renderer needs,
// lifted here so Home's <WeeklyTargetsCard> and the coach's <CoachTargetsPanel>
// resolve them from ONE definition (they render the identical row shape from
// the identical data — the §CONSOLIDAMENTO trigger, before a 2nd hand-copy).

/** A row's display label. The caller resolves per axis: an exercise-name
 *  resolver for `exercise` rows, a muscle-name resolver for `muscle` rows
 *  (the UI passes `(slug) => t(muscleKey(slug))`). Kept i18n-free here. */
export const weeklyTargetRowLabel = (
  row: WeeklyTargetRow,
  resolveExerciseName: (slug: string) => string,
  resolveMuscleName: (slug: string) => string,
): string =>
  row.axis === "exercise"
    ? resolveExerciseName(row.key)
    : resolveMuscleName(row.key);

/** Scale for the neutral (untargeted) bars in a degraded/mixed meter: the
 *  busiest untargeted row's actual, floored at 1 so a bar never divides by
 *  zero (and `Math.max(1)` over no untargeted rows still yields 1). */
export const weeklyTargetDegradedMax = (
  rows: ReadonlyArray<WeeklyTargetRow>,
): number =>
  Math.max(1, ...rows.filter((r) => r.target == null).map((r) => r.actual));
